// /asistente/:botId/ajustes — the agent's operation settings as a DESTINATION
//. Before this, hours/style/handoff lived only in a modal inside the flow
// editor, while onboarding copy pointed people at "Configuración": the two
// most-asked-for knobs (horario, mensaje fuera de horario) had no URL. Same
// form as the editor modal (AgentSettingsForm); this frame owns load/save.

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { AgentSettingsForm } from './AgentSettingsForm.js'
import { Button, PageHeader, Spinner, toast } from '../../design-system/index.js'
import { type AgentSettings, ownerFlowGateway } from '../admin/flows-data.js'
import { KbPanel } from '../admin/KbPanel.js'

export function AgentSettingsPage(): JSX.Element {
  const { botId = '' } = useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const settings = ownerFlowGateway(botId).settings!
  const { data, isLoading } = useQuery({
    queryKey: ['agent-settings', botId],
    queryFn: () => settings.load(),
  })
  const [form, setForm] = useState<AgentSettings | null>(null)
  const [busy, setBusy] = useState(false)
  const value = form ?? data ?? null

  async function save(): Promise<void> {
    if (!value) return
    setBusy(true)
    try {
      await settings.save(value)
      await qc.invalidateQueries({ queryKey: ['agent-settings', botId] })
      toast.ok('Ajustes guardados.')
    } catch {
      toast.error('No se pudieron guardar los ajustes.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mx-auto w-full max-w-3xl">
      <PageHeader
        breadcrumb={[{ label: 'Agentes', href: '/asistente' }]}
        title="Ajustes del agente"
        subtitle="Horario, estilo de mensajes y la red de seguridad."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={() => void navigate('/asistente')}>
              Volver
            </Button>
            <Button size="sm" loading={busy} disabled={!value} onClick={() => void save()}>
              Guardar ajustes
            </Button>
          </div>
        }
      />
      <div className="mt-5 rounded-2xl border border-line bg-surface p-6">
        {isLoading || !value ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : (
          <AgentSettingsForm value={value} onChange={setForm} />
        )}
      </div>
    </div>
  )
}

// /asistente/:botId/conocimiento — the knowledge base as a destination.
export function AgentKnowledgePage(): JSX.Element {
  const { botId = '' } = useParams()
  const navigate = useNavigate()
  const gateway = ownerFlowGateway(botId)

  return (
    <div className="mx-auto w-full max-w-3xl">
      <PageHeader
        breadcrumb={[{ label: 'Agentes', href: '/asistente' }]}
        title="Conocimiento del agente"
        subtitle="Las fuentes con las que responde: sube documentos o páginas y pruébalo."
        actions={
          <Button variant="secondary" size="sm" onClick={() => void navigate('/asistente')}>
            Volver
          </Button>
        }
      />
      <div className="mt-5 rounded-2xl border border-line bg-surface p-6">
        <KbPanel scopeKey={gateway.scopeKey} api={gateway.kb!} />
      </div>
    </div>
  )
}
